import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { DateField } from '@/components/form/fields/date/DateInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Form } from '@/components/form/Form';
import { CopyFromUserAction } from '@/components/form/fields/CopyFromUserAction';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { isWeekend, isBefore, startOfDay, addDays } from 'date-fns';
import { AddressRequest } from '@/services/api/contracts/address';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { useCreateCustomerAddress } from '@/services/api/hooks/address';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, useWatch, Control } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import { formatCurrency } from '@/utils/currency';

const isDateDisabled = (date: Date) => {
  if (isWeekend(date)) return true;
  
  let daysToAdd = 2;
  const cursor = new Date();
  while (daysToAdd >= 0) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) {
      daysToAdd--;
    }
  }
  return isBefore(date, startOfDay(cursor));
};

const BaseDeliveryFormSchema = z.object({
  userName: z.string().min(1, 'Required'),
  userEmail: z.string().email('Invalid email').or(z.literal('')).or(z.literal('na')),
  personalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  userMobile: z.string().optional(),
  noCorporateEmail: z.boolean().default(false),
  isAsapExpedite: z.boolean().default(false),
  requiredByDate: z.date({
    required_error: 'Please select a required by date',
    invalid_type_error: 'Please select a required by date'
  }),
  address: AddressRequest.omit({ name: true }).extend({
    name: z.string().optional(),
    saveToAddressBook: z.boolean().optional()
  }),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  customerReference: z.string().optional(),
  notes: z.string().optional()
});

const DeliveryFormSchema = BaseDeliveryFormSchema.superRefine((data, ctx) => {
  if (!data.noCorporateEmail && (!data.userEmail || data.userEmail.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required unless checked",
      path: ["userEmail"],
    });
  }
});

type DeliveryFormFields = z.infer<typeof DeliveryFormSchema>;

interface DeployInventoryDeliveryModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  cart: Record<string, number>;
  selectedBundleId: string | null;
  invoiceItems: Array<{ rateCardId?: string; name: string; quantity: number; price: number; total: number }>;
  deployedHardware: Array<{ name: string; quantity: number; sku: string }>;
  totalCost: number;
  uniqueUsers: Array<{ name: string; email: string | null; rawValue: string }>;
  onSuccessCallback: () => void;
}

export const DeployInventoryDeliveryModal: React.FC<DeployInventoryDeliveryModalProps> = ({ mspId, customerId, cart, selectedBundleId, invoiceItems, deployedHardware, totalCost, uniqueUsers, onSuccessCallback, ...props }) => {
  const createServiceRequest = useCreateServiceRequest();
  const createCustomerAddress = useCreateCustomerAddress();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(mspId, customerId);
  const [company] = useCompany(mspId, customerId);
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);

  // Typeahead state
  const [isManualMode, setIsManualMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{name: string, email: string | null} | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return uniqueUsers;
    const q = searchQuery.toLowerCase();
    return uniqueUsers.filter(u => u.name.toLowerCase().includes(q) || (u.email && u.email.toLowerCase().includes(q)));
  }, [uniqueUsers, searchQuery]);

  const handleSelectUser = (u: {name: string, email: string | null}) => {
    setSelectedUser(u);
    setIsDropdownOpen(false);
    setIsManualMode(false);
    methods.setValue('userName', u.name, { shouldValidate: true, shouldDirty: true });
    if (u.email) {
      methods.setValue('userEmail', u.email, { shouldValidate: true, shouldDirty: true });
      methods.setValue('noCorporateEmail', false, { shouldValidate: true, shouldDirty: true });
    } else {
      methods.setValue('userEmail', 'na', { shouldValidate: true, shouldDirty: true });
      methods.setValue('noCorporateEmail', true, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleManualMode = () => {
    setIsManualMode(true);
    setIsDropdownOpen(false);
    setSelectedUser(null);
    methods.setValue('userName', searchQuery, { shouldValidate: true, shouldDirty: true });
    methods.setValue('userEmail', '', { shouldValidate: false });
    methods.setValue('noCorporateEmail', false, { shouldValidate: false });
  };

  const clearSelection = () => {
    setSelectedUser(null);
    setIsManualMode(false);
    setSearchQuery('');
    methods.setValue('userName', '', { shouldValidate: false });
    methods.setValue('userEmail', '', { shouldValidate: false });
    methods.setValue('noCorporateEmail', false, { shouldValidate: false });
  };

  const defaultRequiredByDate = useMemo(() => {
    let daysToAdd = 2;
    const cursor = new Date();
    while (daysToAdd >= 0) {
      cursor.setDate(cursor.getDate() + 1);
      if (!isWeekend(cursor)) {
        daysToAdd--;
      }
    }
    return cursor;
  }, []);

  const methods = useForm<DeliveryFormFields>({
    resolver: zodResolver(DeliveryFormSchema),
    defaultValues: {
      userName: '',
      userEmail: '',
      personalEmail: '',
      userMobile: '',
      noCorporateEmail: false,
      isAsapExpedite: false,
      requiredByDate: defaultRequiredByDate,
      deliveryContactName: '',
      deliveryContactEmail: '',
      deliveryContactMobile: '',
      customerReference: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      setIsManualMode(false);
      setSearchQuery('');
      setSelectedUser(null);
      setUseCustomerAddress(false);
      methods.reset({
        userName: '',
        userEmail: '',
        personalEmail: '',
        userMobile: '',
        noCorporateEmail: false,
        isAsapExpedite: false,
        requiredByDate: defaultRequiredByDate,
        deliveryContactName: '',
        deliveryContactEmail: '',
        deliveryContactMobile: '',
        customerReference: '',
        notes: ''
      });
    }
  }, [props.isOpen, defaultRequiredByDate, methods]);

  const noCorporateEmail = methods.watch('noCorporateEmail');
  const isAsapExpedite = methods.watch('isAsapExpedite');

  useEffect(() => {
    if (isAsapExpedite) {
      methods.setValue('requiredByDate', addDays(new Date(), 1), { shouldValidate: true, shouldDirty: true });
    } else {
      methods.setValue('requiredByDate', null as any, { shouldValidate: true, shouldDirty: true });
      methods.clearErrors('requiredByDate');
    }
  }, [isAsapExpedite, methods]);

  useEffect(() => {
    if (noCorporateEmail) {
      methods.setValue('userEmail', 'na', { shouldValidate: true, shouldDirty: false });
    } else if (methods.getValues('userEmail') === 'na') {
      methods.setValue('userEmail', '', { shouldValidate: true, shouldDirty: false });
    }
  }, [noCorporateEmail, methods]);

  const handleAddressChange = useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: DeliveryFormFields) => {
    const finalCustomerReference = data.customerReference?.trim() || data.userName || 'No Reference Provided';
    
    const { companyId, partnerId, saveToAddressBook, ...cleanAddress } = data.address as any;
    
    let finalAddress = { ...cleanAddress };

    if (saveToAddressBook) {
      try {
        const savedAddr = await createCustomerAddress.mutateAsync({
          ...cleanAddress,
          name: cleanAddress.name || 'Saved Address',
          mspId,
          customerId
        });
        const { id, companyId: cid, partnerId: pid, ...restSaved } = savedAddr as any;
        finalAddress = restSaved;
      } catch (e) {
        toast.error('Failed to save address to address book. Continuing with request...');
      }
    }

    const resolvedAddressName = finalAddress.name || 'Delivery Address';

    const finalPayload = {
      requestContext: {
        requestType: 'DeployInventory',
        requestedBy: user?.email || 'Unknown',
        mspId,
        mspName: partner?.name || '',
        customerId,
        customerName: company?.name || '',
        raisedDateTime: new Date().toISOString(),
        customerReference: finalCustomerReference,
        submissionSource: 'DaaS Portal UI',
        haloClientId: company?.haloClientId || null,
        haloGeneralUserId: company?.haloGeneralUserId || null
      },
      financialContext: {
        totalEstimatedCost: Math.round(totalCost * 100),
        totalCost: Math.round(totalCost * 100),
        inferredServices: invoiceItems.map(item => ({
          rateCardId: item.rateCardId,
          name: item.name,
          quantity: item.quantity,
          price: Math.round(item.price * 100)
        }))
      },
      userContext: {
        userName: data.userName,
        userEmail: data.userEmail,
        personalEmail: data.personalEmail || undefined,
        userMobile: data.userMobile || undefined,
        noCorporateEmail: data.noCorporateEmail
      },
      deployment: {
        isRequired: true,
        logistics: {
          schedule: {
            requestedDate: data.requiredByDate.toISOString(),
            isAsapExpedite: data.isAsapExpedite
          },
          address: { ...finalAddress, name: resolvedAddressName },
          siteContact: {
            contactName: data.deliveryContactName,
            contactEmail: data.deliveryContactEmail,
            contactMobile: data.deliveryContactMobile,
          },
          notes: data.notes
        },
        items: deployedHardware.map(hw => ({
          category: hw.name,
          sku: hw.sku,
          serialNumber: null,
          assetTag: null,
          quantity: hw.quantity,
          warranty: { expiryDate: null, hasAdrWarranty: false, lifecyclePack: "No" }
        }))
      },
      collection: {
        isRequired: false,
        items: []
      }
    };
    
    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'DeployInventory',
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });
      
      toast.success('Service Request Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  useDirtyFormGuard(isDirty);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Deploy Inventory</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the final shipping and contact details for this deployment.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column: The Receipt */}
              <div className="space-y-6">
                <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                  <h3 className="text-lg font-bold border-b pb-3 mb-4">Deployment Summary</h3>
                  
                  {deployedHardware && deployedHardware.length > 0 && (
                    <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                      <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Deploy:</p>
                      <ul className="space-y-1 text-gray-600 list-disc list-inside">
                        {deployedHardware.map((item, index) => (
                          <li key={`hw-${index}`}>
                            <span className="font-medium">{item.quantity}x</span> {item.name} <span className="opacity-75 text-xs ml-1">({item.sku})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-4 flex-1">
                    {invoiceItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-start text-sm">
                        <div className="pr-4">
                          <span className="font-medium">{item.quantity}x</span> {item.name}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {formatCurrency(item.total, company?.region?.code)}
                        </div>
                      </div>
                    ))}


                  </div>
                  
                  <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                    <span>Estimated Total</span>
                    <span>{formatCurrency(totalCost, company?.region?.code)}</span>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4">
                  <div>
                    <TextField id="customerReference" label="Customer/MSP Reference" placeholder="This will appear on the invoice" optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
                </div>
                
                <div className="border p-4 rounded space-y-4">
                  <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
                </div>
              </div>

              {/* Right Column: Logistics & Data Entry */}
              <div className="space-y-6">
                <div className="border p-4 rounded space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-bold">User Details</h4>
                    {(selectedUser || isManualMode) && (
                      <button type="button" onClick={clearSelection} className="text-xs text-blue-600 hover:underline">
                        Clear Selection
                      </button>
                    )}
                  </div>
                  {!selectedUser && !isManualMode ? (
                    <div className="relative" ref={dropdownRef}>
                      <label className="text-secondary-std block text-sm font-medium leading-6 mb-1">
                        User Name <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setIsDropdownOpen(true);
                        }}
                        onFocus={() => setIsDropdownOpen(true)}
                      />
                      {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                          {filteredUsers.length > 0 ? (
                            filteredUsers.map((u, i) => (
                              <button
                                key={i}
                                type="button"
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 flex flex-col"
                                onClick={() => handleSelectUser(u)}
                              >
                                <span className="font-medium text-gray-900">{u.name}</span>
                                {u.email && <span className="text-sm text-gray-500">{u.email}</span>}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-gray-500 italic">No users found matching "{searchQuery}"</div>
                          )}
                          <button
                            type="button"
                            className="w-full text-left px-4 py-3 bg-gray-50 border-t border-gray-200 text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                            onClick={handleManualMode}
                          >
                            + Add New User {searchQuery ? `"${searchQuery}"` : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className={selectedUser ? 'opacity-50 pointer-events-none' : ''}>
                        <TextField id="userName" label="User Name" />
                      </div>
                      <div className="space-y-4">
                        <div className={methods.watch('noCorporateEmail') ? 'opacity-50 pointer-events-none' : ''}>
                          <EmailField 
                            id="userEmail" 
                            label={`Corporate Email${methods.watch('noCorporateEmail') ? '' : ''}`} 
                            disabled={methods.watch('noCorporateEmail') || !!selectedUser}
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                          <input type="checkbox" {...methods.register('noCorporateEmail')} disabled={!!selectedUser && !!selectedUser.email} className="w-4 h-4 rounded border-gray-300 text-primary-700 disabled:opacity-50" />
                          User does not have corporate email
                        </label>
                        <EmailField id="personalEmail" label="Personal Email (For New Hires)" optional={true} />
                        <TextField id="userMobile" label="Personal Mobile Number" optional={true} />
                      </div>
                    </div>
                  )}
                </div>
                
                <SmartUserSelector 
                  methods={methods as any} 
                  uniqueUsers={uniqueUsers} 
                  onSelect={() => {}} 
                  isSimpleContactMode={true}
                  title="Delivery Contact"
                  headerAction={
                    <CopyFromUserAction 
                      control={methods.control} 
                      setValue={methods.setValue} 
                      getValues={methods.getValues} 
                      sourceNameField="userName" 
                      sourcePrimaryEmailField="userEmail" 
                      sourceFallbackEmailField="personalEmail" 
                      sourceMobileField="userMobile"
                      targetNameField="deliveryContactName" 
                      targetEmailField="deliveryContactEmail" 
                      targetMobileField="deliveryContactMobile"
                    />
                  }
                  nameField="deliveryContactName"
                  corpEmailField="deliveryContactEmail"
                  mobileNumberField="deliveryContactMobile"
                  nameLabel="Delivery Contact Name"
                  corpEmailLabel="Delivery Contact Email"
                  mobileNumberLabel="Delivery Contact Mobile"
                  personalEmailOptional={false}
                  mobileNumberOptional={false}
                />

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery Address</h4>
                  <AddressBookSelector 
                    mspId={mspId}
                    customerId={customerId}
                    onChange={handleAddressChange}
                  />
                  {methods.formState.errors.address && (
                    <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                  )}
                  <div className="space-y-2">
                    <div className={isAsapExpedite ? 'hidden' : ''}>
                      <DateField id="requiredByDate" label="Required By Date" excludePastDates={true} disabledPredicate={isDateDisabled} />
                    </div>
                    <div className="mt-2">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                        <input type="checkbox" {...methods.register('isAsapExpedite')} className="w-4 h-4 rounded border-gray-300 text-primary-700" />
                        ASAP - Expedite
                      </label>
                      {isAsapExpedite && (
                        <p className="text-xs italic text-amber-600 ml-6 mt-1">
                          This may incur additional charges
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={createServiceRequest.isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={createServiceRequest.isLoading}
            >
              {createServiceRequest.isLoading ? 'Submitting...' : 'Confirm & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
